import * as Yup from "yup";

// RFC2822 Email Validation https://regexr.com/3e48o
const regexEmail =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g;

/* 
  In yup version 1, they loosed the build-in email validation, so we override it with the regex above.
  https://github.com/jquense/yup?tab=readme-ov-file#stringemailmessage-string--function-schema
*/
Yup.addMethod(Yup.string, "email", function validateEmail(message) {
  return this.matches(regexEmail, {
    message,
    name: "email",
    excludeEmptyString: true
  });
});

export const validationMessages = {
  required: "This value should not be blank.",
  email: "Invalid e-mail address.",
  percentage: "Must be between 0 and 100.",
  typeErrorNumber: "Must be a number.",
  description: "Description must be at most 1024 characters.",
  positive: "Must be a positive number.",
  integer: "Must be round number.",
  number: "Must be a number.",
  recurrentPrice: "Must be from 0.99 to 999.99.",
  appleRecurrentPrice: "Must be from 0.99 to 999.99.",
  decimalEnding: "Must end with 0.49 or 0.99.",
  roundMinute: "Must be round number.",
  minOne: "Minimum is 1.",
  alphanumeric: "This value should contain only letters and numbers.",
  url: "This value is not a valid URL.",
  adTagsMax5: "You can use up to 5 Ad Tags for a position.",
  membersMaxActiveSessions: "Must be between 1 and 10."
};

/* 
    https://github.com/jquense/yup/blob/master/CHANGELOG.md#0310-2020-11-23 
    array().required() will no longer consider an empty array missing and required checks will pass.
    plain objects and arrays are no long cast to strings automatically
*/
const validationSchemas = {
  membersMaxActiveSessions: Yup.number(validationMessages.number)
    .typeError(validationMessages.number)
    .min(1, validationMessages.membersMaxActiveSessions)
    .max(10, validationMessages.membersMaxActiveSessions)
    .nullable(),
  requiredAlphanumeric: Yup.string()
    .required(validationMessages.required)
    .matches(/^[a-zA-Z0-9]+$/, validationMessages.alphanumeric),
  required: Yup.string().required(validationMessages.required).nullable(),
  requiredDate: Yup.string().required(validationMessages.required), // in FormContainer if no initialValue for the date field we set null for value which passes the nullable() condition in upper row
  requiredImage: Yup.string().test(
    "no-delete",
    "Image is required.",
    (value) => (value?.includes("delete") ? false : true)
  ),
  requiredMultiField: Yup.array()
    .required(validationMessages.required)
    .min(1, "At least 1 item must be selected."),
  email: Yup.string().email(validationMessages.email).nullable(),
  ip: Yup.string()
    .required(validationMessages.required)
    .matches(/(^(\d{1,3}\.){3}(\d{1,3})$)/, {
      message: "Invalid IP address",
      excludeEmptyString: true
    })
    .test("ip", "Invalid IP address", (value) => {
      return value === undefined || value.trim() === ""
        ? true
        : value.split(".").find((i) => parseInt(i, 10) > 255) === undefined;
    }),
  requiredEmail: Yup.string()
    .required(validationMessages.required)
    .email(validationMessages.email)
    .nullable(),
  percentageNotRequired: Yup.number()
    .min(0, validationMessages.percentage)
    .max(100, validationMessages.percentage)
    .typeError(validationMessages.typeErrorNumber)
    .nullable(),
  percentage: Yup.number()
    .required(validationMessages.required)
    .min(0, validationMessages.percentage)
    .max(100, validationMessages.percentage)
    .typeError(validationMessages.typeErrorNumber)
    .nullable(),
  requiredIntegerPercentage: Yup.number()
    .required(validationMessages.required)
    .integer(validationMessages.integer)
    .min(0, validationMessages.percentage)
    .max(100, validationMessages.percentage)
    .typeError(validationMessages.typeErrorNumber)
    .nullable(),
  description: Yup.string()
    .max(1024, validationMessages.description)
    .nullable(),
  requiredPositiveNumber: Yup.number(validationMessages.number)
    .required(validationMessages.required)
    .typeError(validationMessages.number)
    .positive(validationMessages.positive)
    .nullable(),
  requiredIntegerNumber: Yup.number(validationMessages.number)
    .required(validationMessages.required)
    .typeError(validationMessages.number)
    .positive(validationMessages.positive)
    .integer(validationMessages.integer)
    .nullable(),
  recurrentPrice: Yup.number(validationMessages.number)
    .required(validationMessages.required)
    .typeError(validationMessages.number)
    .min(0.99, validationMessages.recurrentPrice)
    .max(999.99, validationMessages.recurrentPrice)

    .nullable(),
  appleRecurrentPrice: Yup.number(validationMessages.number)
    .required(validationMessages.required)
    .typeError(validationMessages.number)
    .min(0.99, validationMessages.appleRecurrentPrice)
    .max(999.99, validationMessages.appleRecurrentPrice)
    .test(
      "is-decimal",
      validationMessages.decimalEnding,
      (value) =>
        `${value}`.match("^\\d+\\.\\d{0,2}$") &&
        (`${value}`.includes(".49") || `${value}`.includes(".99"))
    )
    .nullable(),
  minutesSeconds: Yup.string()
    .required(validationMessages.required)
    .test(
      "valid-minutes-seconds",
      "Enter a valid duration: minutes (1-1440) or minutes:seconds.",
      (value) => {
        if (!value) return false;
        const trimmed = value.toString().trim();
        // Check format: only digits and at most one colon
        if (!/^\d+(:?\d+)?$/.test(trimmed)) {
          return false;
        }
        const parts = trimmed.split(":");
        // Just minutes (e.g., "30" or "1440")
        if (parts.length === 1) {
          const minutes = parseInt(parts[0], 10);
          return !isNaN(minutes) && minutes >= 1 && minutes <= 1440;
        }
        // Minutes:Seconds format (e.g., "30:45")
        if (parts.length === 2) {
          const minutes = parseInt(parts[0], 10);
          const seconds = parseInt(parts[1], 10);
          return (
            !isNaN(minutes) &&
            !isNaN(seconds) &&
            minutes >= 1 &&
            minutes <= 1440 &&
            seconds >= 0 &&
            seconds <= 59
          );
        }
        return false;
      }
    ),
  url: Yup.string().url(validationMessages.url),
  requiredUrl: Yup.string()
    .required(validationMessages.required)
    .url(validationMessages.url),
  password: Yup.string()
    .required(validationMessages.required)
    .strict(true)
    .trim("Password cannot start or end with blank space")
    .test({
      name: "password-strength",
      exclusive: false,
      params: {},
      message:
        "Password is too weak. Try to include some upper case letters, numbers and/or special characters.",
      test: function isWeak(value) {
        return value ? this.parent.passwordScore > 2 : false;
      }
    }),
  customValidation: (customValidation, message) =>
    Yup.string().test("custom-validation", message, (value) =>
      customValidation(value)
    )
};

export const buildValidationSchema = ({ fields }) => {
  const validationsObj = fields.reduce((acc, field) => {
    if (field.validationSchema) {
      acc[field.name] = validationSchemas[field.validationSchema];
    }
    if (field.customValidation) {
      acc[field.name] = validationSchemas.customValidation(
        field.customValidation,
        field.customValidationMessage
      );
    }
    if (field.definedValidation) {
      acc[field.name] = field.definedValidation;
    }
    if (
      field.isRequired &&
      !field.definedValidation &&
      !field.validationSchema &&
      field.type !== "creditCard" &&
      field.name !== "bannerImage"
    ) {
      acc[field.name] = validationSchemas.required;
    }
    return acc;
  }, {});
  return Yup.object().shape(validationsObj);
};

export default buildValidationSchema;
